import type { ComponentPropsWithoutRef, KeyboardEvent, MouseEvent } from 'react';
import classnames from 'classnames';
import styles from './InlayPanel.module.scss';

interface InlayPanelProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onSelect'> {
    /** A small heading at the top of the panel. */
    label?: string;
    /** Highlights the panel under the pointer. */
    hoverable?: boolean;
    /** Outlines the panel while it or anything inside it has focus. */
    focusable?: boolean;
    /**
     * Makes the whole panel a selectable card: a button that a click, Enter
     * or Space chooses. It is then also hoverable and focusable.
     */
    onSelect?: () => void;
}

/** A recessed panel inside a larger one, optionally a selectable card. */
export function InlayPanel({
    children,
    label,
    className,
    hoverable = false,
    focusable = false,
    onSelect,
    onClick,
    onKeyDown,
    ...props
}: InlayPanelProps): JSX.Element {
    const isSelectable = onSelect !== undefined;

    const handleClick = (event: MouseEvent<HTMLDivElement>): void => {
        onClick?.(event);
        onSelect?.();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        onKeyDown?.(event);
        // Only on the card itself: a control inside keeps its own Enter and Space.
        if (isSelectable && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect();
        }
    };

    return (
        <div
            className={classnames(
                styles.inlayPanel,
                (hoverable || isSelectable) && styles.hoverable,
                (focusable || isSelectable) && styles.focusable,
                className,
            )}
            role={isSelectable ? 'button' : undefined}
            tabIndex={isSelectable ? 0 : undefined}
            {...props}
            onClick={isSelectable ? handleClick : onClick}
            onKeyDown={isSelectable ? handleKeyDown : onKeyDown}
        >
            {label && <h4 className={styles.label}>{label}</h4>}
            {children}
        </div>
    );
}
